// Admin Dashboard (Phase 4) — visible only to is_admin accounts (the button
// is hidden for everyone else, and api/admin2.js 403s regardless).
// Tabs: Contributions (review queue) · Users (plans) · Queries (usage/cost)
// · Feedback (👎/wrong first). Everything is read-only except the explicit
// actions. Card-based layout so it stays usable on a phone.
import { useCallback, useEffect, useState } from 'react';
import { callAdmin } from './adminApi';
import { equipmentTitle } from '../chat/ChatView';

const PLAN_OPTIONS = ['trial', 'free', 'basic', 'pro', 'chief'];

const cardStyle = {
  background: 'var(--surface-1, #ffffff)',
  border: '1px solid var(--border-soft, #eceff6)',
  borderRadius: 14,
  padding: '12px 14px',
};

const metaStyle = {
  fontFamily: 'var(--mono)',
  fontSize: 10,
  letterSpacing: 0.5,
  color: 'var(--text-3)',
};

function formatDate(iso) {
  return iso ? new Date(iso).toLocaleDateString('en-GB') : '';
}

// Shared loader shell: every tab fetches on mount and exposes refresh().
function useTabData(loader) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await loader());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [loader]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

function TabShell({ loading, error, children }) {
  if (loading) {
    return <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)' }}>Loading…</div>;
  }
  if (error) {
    return <div style={{ color: 'var(--red)', fontSize: 13, padding: '12px 0' }}>{error}</div>;
  }
  return children;
}

// --- Contributions ----------------------------------------------------------
function ContributionsTab() {
  const loader = useCallback(() => callAdmin('list_contributions'), []);
  const { data, loading, error, refresh } = useTabData(loader);
  const [edits, setEdits] = useState({}); // id → edited content
  const [notes, setNotes] = useState({}); // id → reject note
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [info, setInfo] = useState(null); // reward outcome after approve

  async function run(id, fn) {
    setBusyId(id);
    setActionError(null);
    setInfo(null);
    try {
      const result = await fn();
      // Approve responses say whether the +20 reward was granted (there is
      // a monthly cap of rewarded approvals per author).
      if (result && typeof result.rewarded === 'boolean') {
        setInfo(
          result.rewarded
            ? '✓ Approved — author rewarded +20 messages.'
            : `✓ Approved. ${result.reward_note || 'No reward granted.'}`
        );
      }
      await refresh();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  const items = data?.items || [];
  return (
    <TabShell loading={loading} error={error}>
      {actionError && (
        <div style={{ color: 'var(--red)', fontSize: 12.5, marginBottom: 10 }}>{actionError}</div>
      )}
      {info && (
        <div style={{ color: 'var(--green)', fontSize: 12.5, marginBottom: 10 }}>{info}</div>
      )}
      {items.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)', fontSize: 13 }}>
          No pending contributions — the review queue is empty. 🎉
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map(c => {
          const content = edits[c.id] ?? c.content;
          const busy = busyId === c.id;
          return (
            <div key={c.id} style={cardStyle}>
              <div
                style={{
                  ...metaStyle,
                  marginBottom: 8,
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span>📚 {equipmentTitle(c.equipment_path).toUpperCase()}</span>
                <span>{c.email || c.user_id}</span>
                <span>{formatDate(c.created_at)}</span>
              </div>
              <textarea
                value={content}
                onChange={e => setEdits(prev => ({ ...prev, [c.id]: e.target.value }))}
                rows={4}
                aria-label="Contribution content"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  background: 'var(--surface-3, #f1f3f9)',
                  border: '1px solid var(--border, #e3e7f0)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontFamily: 'var(--sans)',
                  color: 'var(--text)',
                  lineHeight: 1.6,
                  resize: 'vertical',
                  outline: 'none',
                }}
              />
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  marginTop: 10,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {content !== c.content && (
                  <button
                    className="btn btn-ghost"
                    disabled={busy}
                    onClick={() =>
                      run(c.id, () => callAdmin('edit_contribution', { id: c.id, content }))
                    }
                    style={{ padding: '7px 14px', fontSize: 11.5 }}
                  >
                    💾 Save edit
                  </button>
                )}
                <button
                  className="btn"
                  disabled={busy}
                  onClick={() => run(c.id, () => callAdmin('approve_contribution', { id: c.id }))}
                  style={{
                    padding: '7px 16px',
                    fontSize: 11.5,
                    background: 'var(--primary, #16192b)',
                    color: '#ffffff',
                  }}
                >
                  {busy ? 'Working…' : '✓ Approve'}
                </button>
                <button
                  className="btn btn-ghost"
                  disabled={busy}
                  onClick={() =>
                    run(c.id, () =>
                      callAdmin('reject_contribution', {
                        id: c.id,
                        admin_notes: (notes[c.id] || '').trim() || undefined,
                      })
                    )
                  }
                  style={{
                    padding: '7px 14px',
                    fontSize: 11.5,
                    borderColor: 'rgba(248,113,113,0.5)',
                    color: 'var(--red)',
                  }}
                >
                  ✕ Reject
                </button>
                <input
                  value={notes[c.id] || ''}
                  onChange={e => setNotes(prev => ({ ...prev, [c.id]: e.target.value }))}
                  placeholder="Reject note (optional)"
                  aria-label="Reject note"
                  style={{
                    flex: '1 1 160px',
                    background: 'var(--surface-1, #ffffff)',
                    border: '1px solid var(--border, #e3e7f0)',
                    borderRadius: 8,
                    padding: '6px 10px',
                    fontSize: 11.5,
                    fontFamily: 'var(--sans)',
                    color: 'var(--text)',
                    outline: 'none',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </TabShell>
  );
}

// Shared confirm + grant call, used by the Users tab and the Feedback
// "Reward +20" shortcut. Returns true on success.
async function confirmAndGrant(userId, email, amount) {
  const label = amount > 0 ? `+${amount}` : `${amount}`;
  if (!window.confirm(`Grant ${label} messages to ${email || userId}?`)) return false;
  await callAdmin('grant_bonus', { user_id: userId, amount });
  return true;
}

const grantButtonStyle = { padding: '6px 10px', fontSize: 11, minHeight: 32 };

// Editable per-user limits: [field, label, min, max] — ranges mirror the
// server-side validation in api/admin2.js set_user_limits.
const EDITABLE_LIMITS = [
  ['max_manuals', 'Manual slots', 0, 50],
  ['monthly_message_limit', 'Monthly messages', 0, 100000],
  ['trial_messages_left', 'Trial left', 0, 100],
];

const limitInputStyle = {
  width: 90,
  background: 'var(--surface-1, #ffffff)',
  border: '1px solid var(--border, #e3e7f0)',
  borderRadius: 8,
  padding: '8px 8px',
  fontSize: 12,
  fontFamily: 'var(--sans)',
  color: 'var(--text)',
  outline: 'none',
  minHeight: 36,
};

// --- Users ------------------------------------------------------------------
function UsersTab() {
  const loader = useCallback(() => callAdmin('list_users'), []);
  const { data, loading, error, refresh } = useTabData(loader);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [customAmounts, setCustomAmounts] = useState({}); // user_id → input text

  async function changePlan(userId, plan) {
    setBusyId(userId);
    setActionError(null);
    try {
      await callAdmin('set_plan', { user_id: userId, plan });
      await refresh();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  async function grant(userId, email, amount) {
    setActionError(null);
    setBusyId(userId);
    try {
      const done = await confirmAndGrant(userId, email, amount);
      if (done) {
        setCustomAmounts(prev => ({ ...prev, [userId]: '' }));
        await refresh();
      }
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  function grantCustom(userId, email) {
    const amount = parseInt(customAmounts[userId], 10);
    if (!Number.isInteger(amount) || amount === 0) {
      setActionError('Custom amount must be a non-zero integer (e.g. 15 or -10).');
      return;
    }
    grant(userId, email, amount);
  }

  // --- per-user limit overrides (✎ Edit) ---
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({}); // field → input text

  function startEdit(u) {
    setActionError(null);
    setEditingId(u.user_id);
    setEditValues({
      max_manuals: String(u.max_manuals ?? 0),
      monthly_message_limit: String(u.monthly_message_limit ?? 0),
      trial_messages_left: String(u.trial_messages_left ?? 0),
    });
  }

  async function saveLimits(u) {
    const payload = { user_id: u.user_id };
    for (const [field, label, min, max] of EDITABLE_LIMITS) {
      const value = Number(editValues[field]);
      if (!Number.isInteger(value) || value < min || value > max) {
        setActionError(`${label} must be an integer between ${min} and ${max}.`);
        return;
      }
      payload[field] = value;
    }
    if (
      !window.confirm(
        `Save limits for ${u.email || u.user_id}? Manual slots ${payload.max_manuals}, ` +
          `monthly messages ${payload.monthly_message_limit}, trial left ${payload.trial_messages_left}.`
      )
    ) {
      return;
    }
    setActionError(null);
    setBusyId(u.user_id);
    try {
      await callAdmin('set_user_limits', payload);
      setEditingId(null);
      await refresh();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  const items = data?.items || [];
  return (
    <TabShell loading={loading} error={error}>
      {actionError && (
        <div style={{ color: 'var(--red)', fontSize: 12.5, marginBottom: 10 }}>{actionError}</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(u => (
          <div
            key={u.user_id}
            style={{
              ...cardStyle,
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, wordBreak: 'break-all' }}>
                {u.email || u.user_id} {u.is_admin && <span title="Admin">🛠</span>}
              </div>
              <div style={{ ...metaStyle, marginTop: 4 }}>
                TRIAL LEFT: {u.trial_messages_left} · USED: {u.messages_used_this_month}/
                {u.monthly_message_limit} · BONUS: {u.bonus_messages || 0} · MANUALS:{' '}
                {u.manuals_count}/{u.max_manuals}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  marginTop: 8,
                }}
              >
                {[10, 20, 50].map(n => (
                  <button
                    key={n}
                    className="btn btn-ghost"
                    disabled={busyId === u.user_id}
                    onClick={() => grant(u.user_id, u.email, n)}
                    style={grantButtonStyle}
                  >
                    +{n}
                  </button>
                ))}
                <input
                  value={customAmounts[u.user_id] || ''}
                  onChange={e =>
                    setCustomAmounts(prev => ({ ...prev, [u.user_id]: e.target.value }))
                  }
                  placeholder="±N"
                  aria-label={`Custom bonus amount for ${u.email || u.user_id}`}
                  style={{
                    width: 64,
                    background: 'var(--surface-1, #ffffff)',
                    border: '1px solid var(--border, #e3e7f0)',
                    borderRadius: 8,
                    padding: '6px 8px',
                    fontSize: 11.5,
                    fontFamily: 'var(--sans)',
                    color: 'var(--text)',
                    outline: 'none',
                  }}
                />
                <button
                  className="btn btn-ghost"
                  disabled={busyId === u.user_id || !(customAmounts[u.user_id] || '').trim()}
                  onClick={() => grantCustom(u.user_id, u.email)}
                  style={grantButtonStyle}
                >
                  Grant
                </button>
                <button
                  className="btn btn-ghost"
                  disabled={busyId === u.user_id}
                  onClick={() => (editingId === u.user_id ? setEditingId(null) : startEdit(u))}
                  aria-label={`Edit limits for ${u.email || u.user_id}`}
                  style={grantButtonStyle}
                >
                  ✎ Edit
                </button>
              </div>
              {editingId === u.user_id && (
                <div
                  style={{
                    marginTop: 10,
                    padding: '10px 12px',
                    background: 'var(--surface-3, #f1f3f9)',
                    border: '1px solid var(--border-soft, #eceff6)',
                    borderRadius: 10,
                  }}
                >
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {EDITABLE_LIMITS.map(([field, label, min, max]) => (
                      <label key={field} style={{ display: 'block' }}>
                        <span style={{ ...metaStyle, display: 'block', marginBottom: 4 }}>
                          {label.toUpperCase()}
                        </span>
                        <input
                          type="number"
                          min={min}
                          max={max}
                          value={editValues[field] ?? ''}
                          onChange={e =>
                            setEditValues(prev => ({ ...prev, [field]: e.target.value }))
                          }
                          aria-label={`${label} for ${u.email || u.user_id}`}
                          style={limitInputStyle}
                        />
                      </label>
                    ))}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      marginTop: 10,
                    }}
                  >
                    <button
                      className="btn btn-ghost"
                      disabled={busyId === u.user_id}
                      onClick={() => saveLimits(u)}
                      style={grantButtonStyle}
                    >
                      💾 Save
                    </button>
                    <button
                      className="btn btn-ghost"
                      disabled={busyId === u.user_id}
                      onClick={() => setEditingId(null)}
                      style={grantButtonStyle}
                    >
                      Cancel
                    </button>
                    <span style={{ ...metaStyle, flexBasis: '100%', marginTop: 2 }}>
                      Changing plan resets these to plan defaults.
                    </span>
                  </div>
                </div>
              )}
            </div>
            <select
              value={u.plan}
              disabled={busyId === u.user_id}
              onChange={e => changePlan(u.user_id, e.target.value)}
              aria-label={`Plan for ${u.email || u.user_id}`}
              style={{
                background: 'var(--surface-1, #ffffff)',
                border: '1px solid var(--border, #e3e7f0)',
                borderRadius: 999,
                padding: '8px 12px',
                fontSize: 12,
                fontFamily: 'var(--sans)',
                color: 'var(--text)',
                outline: 'none',
              }}
            >
              {PLAN_OPTIONS.map(p => (
                <option key={p} value={p}>
                  {p.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </TabShell>
  );
}

// Model ids must exist in api/_lib/models.js.
const MODEL_FILTER_OPTIONS = [
  ['', 'All models'],
  ['claude-sonnet-4-5', 'Claude Sonnet 4.5'],
  ['claude-haiku-4-5', 'Claude Haiku 4.5'],
  ['deepseek-v4-pro', 'DeepSeek V4 Pro'],
  ['deepseek-v4-flash', 'DeepSeek V4 Flash'],
];

// --- Queries ----------------------------------------------------------------
function QueriesTab() {
  const [filter, setFilter] = useState('');
  const [applied, setApplied] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const loader = useCallback(
    () =>
      callAdmin('list_queries', {
        ...(applied ? { equipment_path: applied } : {}),
        ...(modelFilter ? { model_used: modelFilter } : {}),
      }),
    [applied, modelFilter]
  );
  const { data, loading, error } = useTabData(loader);

  const totals = data?.totals;
  const items = data?.items || [];
  return (
    <div>
      {totals && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {[
            ['TODAY', totals.day],
            ['7 DAYS', totals.week],
            ['30 DAYS', totals.month],
          ].map(([label, value]) => (
            <span
              key={label}
              style={{
                ...metaStyle,
                border: '1px solid var(--border, #e3e7f0)',
                borderRadius: 999,
                padding: '6px 12px',
                background: 'var(--surface-1, #ffffff)',
              }}
            >
              {label}: ${Number(value).toFixed(4)}
            </span>
          ))}
          {data?.cost_guard && (
            <span
              style={{
                ...metaStyle,
                border: '1px solid var(--amber-line, rgba(180,83,9,0.3))',
                color:
                  data.cost_guard.today_usd >= data.cost_guard.limit_usd
                    ? 'var(--red)'
                    : 'var(--amber-text, var(--amber))',
                borderRadius: 999,
                padding: '6px 12px',
                background: 'var(--surface-1, #ffffff)',
              }}
            >
              KILL-SWITCH: ${Number(data.cost_guard.today_usd).toFixed(2)} / $
              {Number(data.cost_guard.limit_usd).toFixed(0)} DAILY LIMIT
            </span>
          )}
        </div>
      )}
      <form
        onSubmit={e => {
          e.preventDefault();
          setApplied(filter.trim());
        }}
        style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}
      >
        <select
          value={modelFilter}
          onChange={e => setModelFilter(e.target.value)}
          aria-label="Model filter"
          style={{
            background: 'var(--surface-1, #ffffff)',
            border: '1px solid var(--border, #e3e7f0)',
            borderRadius: 999,
            padding: '8px 12px',
            fontSize: 12,
            fontFamily: 'var(--sans)',
            color: 'var(--text)',
            outline: 'none',
          }}
        >
          {MODEL_FILTER_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter by equipment path (e.g. general)"
          aria-label="Equipment path filter"
          style={{
            flex: '1 1 220px',
            background: 'var(--surface-1, #ffffff)',
            border: '1px solid var(--border, #e3e7f0)',
            borderRadius: 999,
            padding: '8px 14px',
            fontSize: 12,
            fontFamily: 'var(--sans)',
            color: 'var(--text)',
            outline: 'none',
          }}
        />
        <button
          className="btn btn-ghost"
          type="submit"
          style={{ padding: '7px 16px', fontSize: 12 }}
        >
          Filter
        </button>
      </form>

      <TabShell loading={loading} error={error}>
        {items.length === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)', fontSize: 13 }}>
            No queries logged yet.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(q => (
            <div key={q.id} style={cardStyle}>
              <div style={{ fontSize: 13, lineHeight: 1.55, wordBreak: 'break-word' }}>
                {q.question}
              </div>
              <div
                style={{ ...metaStyle, marginTop: 6, display: 'flex', gap: 10, flexWrap: 'wrap' }}
              >
                <span>{q.email || q.user_id}</span>
                <span>{equipmentTitle(q.equipment_path).toUpperCase()}</span>
                <span>{q.model_used}</span>
                <span>${Number(q.cost_usd || 0).toFixed(4)}</span>
                <span>{formatDate(q.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      </TabShell>
    </div>
  );
}

// --- Feedback ----------------------------------------------------------------
const RATING_BADGE = {
  up: { label: '👍 UP', color: 'var(--green)' },
  down: { label: '👎 DOWN', color: 'var(--yellow)' },
  wrong: { label: '⚠ WRONG', color: 'var(--red)' },
};

function FeedbackTab() {
  const loader = useCallback(() => callAdmin('list_feedback'), []);
  const { data, loading, error } = useTabData(loader);
  const [busyId, setBusyId] = useState(null);
  const [rewardedIds, setRewardedIds] = useState({}); // feedback id → true
  const [actionError, setActionError] = useState(null);

  // One-click thank-you for useful feedback, right where it is read.
  async function reward(feedback) {
    setActionError(null);
    setBusyId(feedback.id);
    try {
      const done = await confirmAndGrant(feedback.user_id, feedback.email, 20);
      if (done) setRewardedIds(prev => ({ ...prev, [feedback.id]: true }));
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  const items = data?.items || [];
  return (
    <TabShell loading={loading} error={error}>
      {actionError && (
        <div style={{ color: 'var(--red)', fontSize: 12.5, marginBottom: 10 }}>{actionError}</div>
      )}
      {items.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)', fontSize: 13 }}>
          No feedback yet.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(f => {
          const badge = RATING_BADGE[f.rating] || RATING_BADGE.up;
          return (
            <div key={f.id} style={cardStyle}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ ...metaStyle, color: badge.color, fontWeight: 700 }}>
                  {badge.label}
                </span>
                <span style={metaStyle}>{f.email || f.user_id}</span>
                <span style={metaStyle}>{formatDate(f.created_at)}</span>
                {rewardedIds[f.id] ? (
                  <span style={{ ...metaStyle, color: 'var(--green)' }}>✓ REWARDED +20</span>
                ) : (
                  <button
                    className="btn btn-ghost"
                    disabled={busyId === f.id || !f.user_id}
                    onClick={() => reward(f)}
                    style={{ ...grantButtonStyle, marginLeft: 'auto' }}
                  >
                    🎁 Reward +20
                  </button>
                )}
              </div>
              {f.chat_query_logs?.question && (
                <div
                  style={{ fontSize: 13, lineHeight: 1.55, marginTop: 6, wordBreak: 'break-word' }}
                >
                  {f.chat_query_logs.question}
                </div>
              )}
              {f.comment && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: 'var(--text-2)',
                    marginTop: 6,
                    fontStyle: 'italic',
                  }}
                >
                  “{f.comment}”
                </div>
              )}
            </div>
          );
        })}
      </div>
    </TabShell>
  );
}

// --- Model Control -------------------------------------------------------------
// The global backup switch: default routing, or force ALL traffic to one
// model (e.g. when Anthropic is down → force DeepSeek). The server fails
// safe back to default routing if the forced model is unusable.
function ModelControlTab() {
  const loader = useCallback(() => callAdmin('get_model_mode'), []);
  const { data, loading, error, refresh } = useTabData(loader);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  async function change(mode) {
    setBusy(true);
    setActionError(null);
    try {
      await callAdmin('set_model_mode', { mode });
      await refresh();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <TabShell loading={loading} error={error}>
      {data && (
        <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700 }}>🎛 Global model mode</div>
          <div style={{ fontSize: 12.5, color: 'var(--yellow)', lineHeight: 1.55 }}>
            ⚠ Force mode routes ALL user traffic to this model. Use as backup/failover.
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={data.mode}
              disabled={busy}
              onChange={e => change(e.target.value)}
              aria-label="Global model mode"
              style={{
                background: 'var(--surface-1, #ffffff)',
                border: '1px solid var(--border, #e3e7f0)',
                borderRadius: 999,
                padding: '10px 14px',
                fontSize: 13,
                fontFamily: 'var(--sans)',
                color: 'var(--text)',
                outline: 'none',
                minHeight: 44,
              }}
            >
              {(data.options || []).map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {busy && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Saving…</span>}
          </div>

          <div style={{ ...metaStyle, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>
              ACTIVE:{' '}
              <strong style={{ color: data.mode === 'default' ? 'var(--green)' : 'var(--amber)' }}>
                {data.mode.toUpperCase()}
              </strong>
            </span>
            {data.updated_at && (
              <span>CHANGED: {new Date(data.updated_at).toLocaleString('en-GB')}</span>
            )}
          </div>

          {!data.valid && (
            <div style={{ fontSize: 12.5, color: 'var(--red)', lineHeight: 1.55 }}>
              ⚠ FAIL-SAFE ACTIVE: {data.issue}
            </div>
          )}
          {actionError && <div style={{ fontSize: 12.5, color: 'var(--red)' }}>{actionError}</div>}

          <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.55 }}>
            Changes reach the chat within ~1 minute (the server caches the switch for 60s). The
            actual model used is always written to the query log — check the Queries tab with the
            model filter to see forced traffic.
          </div>
        </div>
      )}
    </TabShell>
  );
}

// --- Shell --------------------------------------------------------------------
const TABS = [
  { key: 'contributions', label: '📝 Contributions', component: ContributionsTab },
  { key: 'users', label: '👥 Users', component: UsersTab },
  { key: 'queries', label: '💬 Queries', component: QueriesTab },
  { key: 'feedback', label: '📣 Feedback', component: FeedbackTab },
  { key: 'model', label: '🎛 Model Control', component: ModelControlTab },
];

export default function AdminDashboard({ onBack }) {
  const [tab, setTab] = useState('contributions');
  const Active = TABS.find(t => t.key === tab)?.component || ContributionsTab;

  return (
    <div style={{ fontFamily: 'var(--sans)', color: 'var(--text)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <button
          className="btn btn-ghost"
          onClick={onBack}
          style={{ padding: '9px 16px', fontSize: 12, minHeight: 40 }}
        >
          ←
        </button>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>🛠 Admin Dashboard</div>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
            Review contributions, manage plans, watch usage and feedback.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className="chip"
            style={
              tab === t.key
                ? {
                    borderColor: 'var(--accent, #3b6fe0)',
                    color: 'var(--accent, #3b6fe0)',
                    background: 'rgba(59,111,224,0.08)',
                    fontWeight: 700,
                  }
                : undefined
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      <Active />
    </div>
  );
}
